import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { SharedHeader } from "@/components/SharedHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import EventGallery from "@/components/EventGallery";
import EventChat from "@/components/EventChat";
import { 
  ArrowLeft, Calendar, Clock, MapPin, Users, DollarSign, 
  Trophy, MessageCircle, Image, Loader2, Shield, Star, Share2
} from "lucide-react";

const EventDetailPage = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { user, token } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    if (eventId) {
      fetchEvent();
      checkRegistration();
    }
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data);
    } catch (err) {
      toast.error("Failed to load event");
      navigate("/home");
    } finally {
      setLoading(false);
    }
  };

  const checkRegistration = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/events/my-events/registered`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const events = response.data.events || [];
      setIsRegistered(events.some(e => e.id === eventId));
    } catch (err) {
      console.log("Could not check registration");
    }
  };

  const handleRSVP = async () => {
    if (!token) {
      toast.error("Please log in to RSVP");
      return;
    }

    setRsvpLoading(true);
    try {
      if (isRegistered) {
        await axios.post(`${API}/events/${eventId}/cancel-rsvp`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("RSVP cancelled");
        setIsRegistered(false);
      } else {
        await axios.post(`${API}/events/rsvp`, { event_id: eventId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("RSVP confirmed!");
        setIsRegistered(true);
      }
      fetchEvent(); // Refresh participant count
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update RSVP");
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: event?.title,
        text: `Check out this event: ${event?.title}`,
        url: url
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#7a6200] animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-[#A1A1AA]">Event not found</p>
      </div>
    );
  }

  const isHost = event.host_id === user?.id;
  const isAdmin = user?.role === "admin" || user?.role === "crm_admin";
  const isFull = event.current_participants >= event.max_participants;
  const spotsLeft = event.max_participants - event.current_participants;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="event-detail-page">
      <SharedHeader 
        activeSection={null}
        setActiveSection={() => {}}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-6 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-ubuntu text-sm">Back</span>
          </button>

          {/* Event Header */}
          <div className="mb-8">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Badge 
                    className={`font-ubuntu text-xs ${
                      event.is_tournament 
                        ? "bg-[#7a6200]/20 text-[#C6A300] border-[#7a6200]" 
                        : "bg-white/10 text-white border-white/20"
                    }`}
                  >
                    {event.is_tournament ? (
                      <><Trophy className="w-3 h-3 mr-1" /> Tournament</>
                    ) : (
                      event.game_type
                    )}
                  </Badge>
                  <span className={`text-xs font-ubuntu capitalize px-2 py-1 rounded ${
                    event.skill_level === "advanced" ? "bg-red-500/20 text-red-400" :
                    event.skill_level === "intermediate" ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-green-500/20 text-green-400"
                  }`}>
                    {event.skill_level === "all" ? "All Levels" : event.skill_level}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-ubuntu border capitalize ${
                    event.status === "upcoming" ? "bg-blue-500/20 text-blue-400 border-blue-500/30" :
                    event.status === "active" ? "bg-green-500/20 text-green-400 border-green-500/30" :
                    event.status === "cancelled" ? "bg-red-500/20 text-red-400 border-red-500/30" :
                    "bg-[#7a6200]/20 text-[#7a6200] border-[#7a6200]/30"
                  }`}>
                    {event.status}
                  </span>
                </div>
                <h1 className="font-bebas text-4xl md:text-5xl tracking-wider text-white">
                  {event.title}
                </h1>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShare}
                  className="border-white/20 text-[#A1A1AA] hover:text-white"
                  data-testid="share-btn"
                >
                  <Share2 className="w-4 h-4" />
                </Button>
                {event.status === "upcoming" && !isHost && (
                  <Button
                    onClick={handleRSVP}
                    disabled={rsvpLoading || (isFull && !isRegistered)}
                    className={isRegistered ? "btn-outline-gold" : "btn-gold"}
                    data-testid="rsvp-btn"
                  >
                    {rsvpLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isRegistered ? (
                      "Cancel RSVP"
                    ) : isFull ? (
                      "Event Full"
                    ) : (
                      "RSVP Now"
                    )}
                  </Button>
                )}
              </div>
            </div>

            <p className="text-[#A1A1AA] font-ubuntu text-lg max-w-3xl">
              {event.description}
            </p>
          </div>

          {/* Event Info Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <Calendar className="w-8 h-8 text-[#7a6200]" />
                <div>
                  <p className="text-[#52525B] text-xs font-ubuntu">Date</p>
                  <p className="text-white font-ubuntu">
                    {new Date(event.date).toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <Clock className="w-8 h-8 text-[#7a6200]" />
                <div>
                  <p className="text-[#52525B] text-xs font-ubuntu">Time</p>
                  <p className="text-white font-ubuntu">{event.time}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <MapPin className="w-8 h-8 text-[#7a6200]" />
                <div>
                  <p className="text-[#52525B] text-xs font-ubuntu">Location</p>
                  <p className="text-white font-ubuntu truncate">{event.location}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <Users className="w-8 h-8 text-[#7a6200]" />
                <div>
                  <p className="text-[#52525B] text-xs font-ubuntu">Spots</p>
                  <p className="text-white font-ubuntu">
                    {event.current_participants}/{event.max_participants}
                    {spotsLeft > 0 && spotsLeft <= 3 && (
                      <span className="text-yellow-400 text-xs ml-1">({spotsLeft} left!)</span>
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Entry Fee */}
          {event.entry_fee > 0 && (
            <Card className="bg-[#0A0A0A] border-[#7a6200]/30 mb-8">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-8 h-8 text-[#7a6200]" />
                  <div>
                    <p className="text-[#52525B] text-xs font-ubuntu">Entry Fee</p>
                    <p className="font-bebas text-2xl text-[#7a6200]">${event.entry_fee}</p>
                  </div>
                </div>
                <p className="text-[#52525B] text-sm font-ubuntu">Payment collected at event</p>
              </CardContent>
            </Card>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-[#0A0A0A] border border-white/10 mb-6">
              <TabsTrigger value="details" className="data-[state=active]:bg-[#7a6200]">
                <Shield className="w-4 h-4 mr-2" />
                Details & Rules
              </TabsTrigger>
              <TabsTrigger value="gallery" className="data-[state=active]:bg-[#7a6200]">
                <Image className="w-4 h-4 mr-2" />
                Photo Gallery
              </TabsTrigger>
              <TabsTrigger value="chat" className="data-[state=active]:bg-[#7a6200]">
                <MessageCircle className="w-4 h-4 mr-2" />
                Event Chat
              </TabsTrigger>
            </TabsList>

            {/* Details Tab */}
            <TabsContent value="details">
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="font-bebas text-xl tracking-wider text-white">
                    EVENT RULES & GUIDELINES
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {event.rules ? (
                    <div className="prose prose-invert max-w-none">
                      <p className="text-[#A1A1AA] font-ubuntu whitespace-pre-wrap">{event.rules}</p>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-[#52525B]">
                      <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p className="font-ubuntu">No specific rules have been set for this event.</p>
                      <p className="font-ubuntu text-sm mt-2">Standard Board2Boston™ guidelines apply.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Gallery Tab */}
            <TabsContent value="gallery">
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="font-bebas text-xl tracking-wider text-white">
                    EVENT PHOTO GALLERY
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <EventGallery 
                    eventId={eventId} 
                    token={token}
                    isAdmin={isAdmin}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Chat Tab */}
            <TabsContent value="chat">
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="font-bebas text-xl tracking-wider text-white">
                    EVENT CHAT
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {token ? (
                    <EventChat eventId={eventId} inline={true} />
                  ) : (
                    <div className="text-center py-8 text-[#52525B]">
                      <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p className="font-ubuntu">Please log in to participate in the event chat.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <footer className="border-t border-white/10 bg-[#0A0A0A] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#52525B] font-ubuntu text-sm">
            © 2026 All Rights Reserved | Board2Boston™ | 713 Consulting and Development
          </p>
          <p className="text-[#52525B] font-ubuntu text-xs mt-2">
            1942 W. Gray St., Box #1259 | Houston, TX 77019 | United States
          </p>
        </div>
      </footer>
    </div>
  );
};

export default EventDetailPage;
